/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Super Admin Authentication Store (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { getAdminToken, setAdminToken } from '../lib/api-client';

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: 'super_admin';
  avatarUrl?: string;
}

export interface AdminAuthState {
  token: string | null;
  adminUser: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setSession: (token: string, user: AdminUser) => void;
  logout: () => void;
  checkSession: () => void;
}

const ADMIN_USER_STORAGE_KEY = 'sophia_admin_user_profile';

export const useAdminAuthStore = create<AdminAuthState>((set) => ({
  token: getAdminToken(),
  adminUser: (() => {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(ADMIN_USER_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })(),
  isAuthenticated: Boolean(getAdminToken()),
  isLoading: false,

  setSession: (token: string, user: AdminUser) => {
    if (typeof window !== 'undefined') {
      setAdminToken(token);
      localStorage.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(user));
    }
    set({ token, adminUser: user, isAuthenticated: true });
  },

  logout: () => {
    if (typeof window !== 'undefined') {
      setAdminToken(null);
      localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
    }
    set({ token: null, adminUser: null, isAuthenticated: false });
  },

  checkSession: () => {
    if (typeof window === 'undefined') return;
    const token = getAdminToken();
    const rawUser = localStorage.getItem(ADMIN_USER_STORAGE_KEY);
    let user = null;
    if (rawUser) {
      try {
        user = JSON.parse(rawUser);
      } catch {
        // Ignore
      }
    }

    if (token && user) {
      set({ token, adminUser: user, isAuthenticated: true });
    } else {
      set({ token: null, adminUser: null, isAuthenticated: false });
    }
  },
}));

// Listen to global 401 unauthorized events to force sign-out
if (typeof window !== 'undefined') {
  window.addEventListener('sophia:admin-unauthorized', () => {
    useAdminAuthStore.getState().logout();
  });
}
